import { useScrollReveal } from "@/hooks/useScrollReveal";

const ACTIVE_CASES = [
  { id: "CS-042", student: "Martín Soto", grade: "2° Medio B", type: "Acoso escolar", status: "En intervención", statusColor: "bg-primary-100 text-primary-700", responsible: "Ana L.", priority: "Alta", days: 3 },
  { id: "CS-041", student: "Emilia Rojas", grade: "8° Básico A", type: "Vulneración de derechos", status: "Crítico", statusColor: "bg-red-100 text-red-700", responsible: "Carlos R.", priority: "Crítica", days: 0 },
  { id: "CS-040", student: "Benjamín Cáceres", grade: "4° Medio C", type: "Conflicto entre pares", status: "En seguimiento", statusColor: "bg-secondary-100 text-secondary-700", responsible: "María G.", priority: "Media", days: 12 },
  { id: "CS-039", student: "Isidora Muñoz", grade: "1° Medio A", type: "Ciberacoso", status: "Cerrado", statusColor: "bg-emerald-100 text-emerald-700", responsible: "Pedro S.", priority: "Alta", days: 0 },
];

const PROTOCOLS = [
  { name: "Protocolo de acoso escolar", activations: 12, lastUsed: "Hace 3 días", icon: "ri-shield-user-line", color: "bg-primary-500" },
  { name: "Protocolo de vulneración de derechos", activations: 8, lastUsed: "Hace 1 semana", icon: "ri-heart-pulse-line", color: "bg-red-500" },
  { name: "Protocolo de ciberacoso", activations: 5, lastUsed: "Hace 2 días", icon: "ri-computer-line", color: "bg-accent-500" },
  { name: "Protocolo de conflicto entre pares", activations: 15, lastUsed: "Ayer", icon: "ri-team-line", color: "bg-secondary-500" },
];

const METRICS = [
  { label: "Casos abiertos", value: "34", sub: "este mes", color: "bg-primary-500", icon: "ri-folder-3-line" },
  { label: "Resolución", value: "87%", sub: "tasa de cierre", color: "bg-secondary-500", icon: "ri-check-double-line" },
  { label: "Tiempo promedio", value: "5.8d", sub: "por caso", color: "bg-accent-500", icon: "ri-time-line" },
  { label: "Protocolos activados", value: "40", sub: "último trimestre", color: "bg-emerald-500", icon: "ri-book-open-line" },
];

export default function ConvivenciaEscolarSection() {
  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.12, selector: ".scroll-reveal", staggerDelay: 100 });

  return (
    <section id="convivencia-escolar" ref={sectionRef} className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
      {/* Subtle bg decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-50/60 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-50/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="scroll-reveal text-xs font-semibold text-primary-600 tracking-widest uppercase bg-primary-50 px-3 py-1 rounded-full inline-block">
            Convivencia Escolar
          </span>
          <h2 className="scroll-reveal text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mt-4 tracking-tight">
            Gestión integral de la convivencia
          </h2>
          <p className="scroll-reveal text-lg text-foreground-500 mt-4 max-w-2xl mx-auto leading-relaxed">
            Registra casos, activa protocolos, asigna responsables y monitorea cada intervención con trazabilidad completa desde la detección hasta la resolución.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Active cases */}
          <div className="lg:col-span-2 scroll-reveal">
            <div className="bg-white rounded-3xl border border-background-200/80 shadow-soft-lg p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-foreground-950">Casos activos</h3>
                  <p className="text-sm text-foreground-400">Seguimiento en tiempo real</p>
                </div>
                <button className="px-4 py-2 bg-primary-500 text-white text-xs font-semibold rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                  <i className="ri-add-line" /> Nuevo caso
                </button>
              </div>

              <div className="space-y-3">
                {ACTIVE_CASES.map((c, i) => (
                  <div key={c.id} className="group flex items-center gap-4 p-4 rounded-2xl bg-background-50/80 hover:bg-background-100/80 transition-all duration-200 cursor-pointer">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.priority === "Crítica" ? "bg-red-500 animate-alert-blink" : c.priority === "Alta" ? "bg-accent-500" : "bg-foreground-300"}`} />
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary-700">{c.student.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-foreground-400 bg-background-200 px-1.5 py-0.5 rounded">{c.id}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.statusColor}`}>{c.status}</span>
                      </div>
                      <p className="text-sm font-semibold text-foreground-900">{c.student} — {c.type}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{c.grade} &middot; {c.responsible} &middot; Última acción: hace {c.days}d</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-foreground-600 group-hover:translate-x-0.5 transition-all hidden sm:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Protocols + Metrics */}
          <div className="flex flex-col gap-6 scroll-reveal">
            {/* Protocol cards */}
            <div className="bg-white rounded-2xl border border-background-200/80 shadow-soft p-5">
              <h4 className="text-sm font-bold text-foreground-800 mb-4">Protocolos más utilizados</h4>
              <div className="space-y-3">
                {PROTOCOLS.map((p) => (
                  <div key={p.name} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-50 transition-colors cursor-pointer">
                    <div className={`w-8 h-8 rounded-lg ${p.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${p.icon} text-white text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground-800 truncate">{p.name}</p>
                      <p className="text-[11px] text-foreground-400">{p.activations} activaciones &middot; {p.lastUsed}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics cards */}
            <div className="grid grid-cols-2 gap-3">
              {METRICS.map((m) => (
                <div key={m.label} className="bg-white rounded-2xl border border-background-200/80 shadow-soft p-4 hover:shadow-soft-md transition-all duration-300 cursor-default">
                  <div className={`w-9 h-9 rounded-xl ${m.color} flex items-center justify-center mb-3`}>
                    <i className={`${m.icon} text-white text-sm`} />
                  </div>
                  <p className="text-2xl font-extrabold text-foreground-950">{m.value}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{m.label}</p>
                  <p className="text-xs text-foreground-300">{m.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}